import { useTranslation, Trans } from "react-i18next";
import Seo from "@/components/Seo";
import Header from "./Header";
import Footer from "./Footer";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function About() {
  const { t } = useTranslation();
  const manifesto = useScrollReveal<HTMLDivElement>();
  const pillarsRef = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });
  const vision = useScrollReveal<HTMLDivElement>();

  const manifestoLines = t("about.manifesto", { returnObjects: true }) as string[];
  const pillars = t("about.pillars", { returnObjects: true }) as { title: string; desc: string }[];

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Seo title={t("seo.about.title")} description={t("seo.about.desc")} path="/about" />
      <Header />
      <main id="main" tabIndex={-1} className="flex-1 outline-none">
        {/* Hero */}
        <section className="w-full px-6 pt-16 pb-20 md:pt-24 md:pb-28">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between ed-eyebrow text-muted-foreground animate-fade-in">
              <span className="inline-flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand ed-blink" />
                {t("about.eyebrow")}
              </span>
              <span>{t("about.side")}</span>
            </div>

            <h1 className="ed-display mt-8 max-w-[12ch] animate-fade-in">
              {t("about.title")}
              <span className="text-brand">.</span>
            </h1>

            <p className="ed-serif mt-10 text-2xl md:text-4xl max-w-3xl text-foreground/85 animate-fade-in-delay-1">
              {t("about.sub")}
            </p>
          </div>
        </section>

        <div className="ed-rule max-w-[1400px] mx-auto" />

        {/* Manifesto */}
        <section className="w-full px-6 py-24 md:py-36">
          <div
            ref={manifesto.ref}
            className={`max-w-[1400px] mx-auto reveal reveal-blur ${manifesto.isVisible ? "visible" : ""}`}
          >
            <div className="grid gap-10 md:grid-cols-[auto_1fr] md:gap-16">
              <span className="ed-eyebrow text-muted-foreground md:pt-4">
                {t("about.manifestoLabel")}
              </span>
              <div>
                <blockquote className="ed-serif text-3xl md:text-5xl lg:text-6xl text-foreground leading-[1.1] space-y-4">
                  {manifestoLines.map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </blockquote>
                <p className="mt-10 ed-eyebrow text-muted-foreground">
                  {t("about.manifestoFoot")}
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="ed-rule max-w-[1400px] mx-auto" />

        {/* Name origin */}
        <section className="w-full px-6 py-24 md:py-36">
          <div className="max-w-[1400px] mx-auto grid gap-10 md:grid-cols-[auto_1fr] md:gap-16">
            <span className="ed-eyebrow text-muted-foreground md:pt-4">
              {t("about.nameLabel")}
            </span>
            <div className="max-w-3xl">
              <h2 className="ed-headline">{t("about.nameTitle")}</h2>
              <div className="mt-8 space-y-6 text-lg md:text-xl text-muted-foreground leading-relaxed">
                <p>
                  <Trans
                    i18nKey="about.namePara1"
                    components={{
                      aurora: <em className="ed-serif text-foreground text-xl md:text-2xl" />,
                      avorio: <em className="ed-serif text-foreground text-xl md:text-2xl" />,
                    }}
                  />
                </p>
                <p>{t("about.namePara2")}</p>
              </div>
            </div>
          </div>
        </section>

        <div className="ed-rule max-w-[1400px] mx-auto" />

        {/* Philosophy pillars */}
        <section className="w-full px-6 py-24 md:py-36">
          <div ref={pillarsRef.ref} className="max-w-[1400px] mx-auto">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <h2 className="ed-display">{t("about.philTitle")}</h2>
              <span className="ed-serif text-2xl md:text-3xl text-muted-foreground">
                {t("about.philSub")}
              </span>
            </div>

            <ol className="mt-16">
              {pillars.map((p, i) => (
                <li
                  key={p.title}
                  className={`group border-t border-border py-10 reveal reveal-up reveal-delay-${i + 1} ${pillarsRef.isVisible ? "visible" : ""} ${i === pillars.length - 1 ? "border-b border-border" : ""}`}
                >
                  <div className="grid gap-6 md:grid-cols-[auto_1fr_2fr] md:gap-12 md:items-baseline">
                    <span className="ed-serif text-3xl md:text-4xl text-muted-foreground group-hover:text-brand transition-colors duration-300">
                      0{i + 1}
                    </span>
                    <h3 className="ed-headline">{p.title}</h3>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      {p.desc}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <div className="ed-rule max-w-[1400px] mx-auto" />

        {/* Vision */}
        <section className="w-full px-6 py-24 md:py-36">
          <div
            ref={vision.ref}
            className={`max-w-[1400px] mx-auto grid gap-10 md:grid-cols-[auto_1fr] md:gap-16 reveal reveal-up ${vision.isVisible ? "visible" : ""}`}
          >
            <span className="ed-eyebrow text-muted-foreground md:pt-4">
              {t("about.futureLabel")}
            </span>
            <div className="max-w-3xl">
              <h2 className="ed-display">{t("about.futureTitle")}</h2>
              <div className="mt-10 space-y-6 text-lg md:text-xl text-muted-foreground leading-relaxed">
                <p>{t("about.futurePara1")}</p>
                <p>{t("about.futurePara2")}</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
